import * as signalR from "@microsoft/signalr";
import { toast } from "react-toastify";
import axiosInstance from "../../../core/data/interface/axiosInstance";
import { getBaseUrl } from "../../../core/data/interface/getBaseUrl";
import { getAuthToken } from "../../../core/data/interface/authStorage";
import {
  setPermissionKeys,
  setEnabledModules,
} from "../../../core/data/redux/permissionsSlice";
import {
  setUser
} from "../../../core/data/redux/authSlice";
import { store } from "../../../core/data/redux/store"; // Import the store directly

const BASE_URL = getBaseUrl(false);
const MAX_RETRY_ATTEMPTS = 5;
const RETRY_DELAY_MS = 5000;

export type ConnectionState =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting";

/** Payload of the server's "DataChanged" push — see IRealtimeUpdateService. */
/**
 * A vehicle alert (van not leaving home, job not started, and the other
 * VanAlertConstants types). Sent by VanAlertService to the owning company's
 * "vehicles" module room, so grantees of `vehicles.` receive it too.
 *
 * Every field is optional and both casings are accepted: the server builds the
 * payload with PascalCase property names, and the wire casing depends on the
 * hub's serializer configuration rather than on anything this file controls.
 */
/**
 * Wildcard module on a DataChanged payload: "this affects every screen, refetch".
 * Used when what changed is the caller VISIBILITY (permissions, a share being
 * granted or revoked) rather than any particular row.
 */
export const DATA_CHANGED_ALL = "*";

export interface VanAlertPayload {
  alertId?: number | string;
  alertType?: string;
  vehicleNumber?: string;
  orderNumber?: string | number;
  delayMinutes?: number;
  severity?: string;
  message?: string;
  latitude?: number | string;
  longitude?: number | string;
  triggeredAt?: string;
  [key: string]: any;
}

export interface DataChangedPayload {
  /** TenantModuleMap prefix, e.g. "clothing", "employees", "sop". */
  module: string;
  /** Company that OWNS the changed row — may not be the viewer's company. */
  tenantId: number;
  /** What changed, e.g. "ClothingRequest". */
  entity: string;
  /** "created" | "updated" | "deleted". */
  action: string;
  /** Key of the changed row, when there is a single one. */
  id?: number | string | null;
  at: string;
}

interface Notification {
  id: number;
  title: string;
  body: string;
  typeName: string;
  createdAt: string;
  isRead: boolean;
  isDismissed: boolean;
  templateName: string;
}
const baseURL = getBaseUrl(true);

class NotificationService {
  private connection: signalR.HubConnection | null = null;
  private notificationCallbacks: ((notification: Notification) => void)[] = [];
  private dataChangedCallbacks: ((payload: DataChangedPayload) => void)[] = [];
  // Fired when this user's read/dismiss state changed anywhere (another tab or device).
  private notificationStateCallbacks: (() => void)[] = [];
  private vanAlertCallbacks: ((payload: VanAlertPayload) => void)[] = [];
  private countCallbacks: ((count: number) => void)[] = [];
  private notificationsRequest: Promise<Notification[]> | null = null;
  private unreadCountRequest: Promise<number> | null = null;
  private retryCount = 0;
  private isExplicitlyStopped = false;
  private connectionState: ConnectionState = "disconnected";

  // 30-second response cache shared across all hook instances
  private readonly CACHE_TTL = 30_000;
  private notificationsCache: { data: Notification[]; at: number } | null = null;
  private unreadCountCache: { data: number; at: number } | null = null;

  public invalidateCache(): void {
    this.notificationsCache = null;
    this.unreadCountCache = null;
  }
  public async restartConnection(): Promise<void> {
    console.log("Restarting SignalR connection...");
    await this.stopConnection();
    this.isExplicitlyStopped = false; // Reset the flag to allow reconnection
    await this.startConnection();
  }

  public async startConnection(): Promise<void> {
    if (
      this.isExplicitlyStopped ||
      this.connectionState === "connected" ||
      this.connectionState === "connecting" ||
      this.connectionState === "reconnecting"
    )
      return;

    this.connectionState = "connecting";

    const token = getAuthToken();

    if (!token) {
      console.error("No auth token available");
      this.connectionState = "disconnected";

      return;
    }

    try {
              console.log("connection restart");
      
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${BASE_URL}/notificationHub`, {
          withCredentials: false,
          accessTokenFactory: () => token,
          // Same transports in dev as in production. Pinning dev to LongPolling
          // kept one HTTP request open permanently — it shows as a request stuck
          // on "pending" and occupies one of the browser's ~6 connections per
          // host, so ordinary API calls queued behind it. WebSockets needs one
          // socket and leaves the connection pool alone; the fallbacks remain if
          // the proxy cannot upgrade.
          transport:
            signalR.HttpTransportType.WebSockets |
            signalR.HttpTransportType.ServerSentEvents |
            signalR.HttpTransportType.LongPolling,
        })
        .configureLogging(signalR.LogLevel.Information)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.elapsedMilliseconds > 60) {
              return null;
            }
            return RETRY_DELAY_MS;
          },
        })
        .build();

      await this.connection.start();
      this.connectionState = "connected";
      console.log("SignalR Connected");
      this.retryCount = 0;

      this.registerHubMethods();

      this.connection.onreconnected(() => {
        this.connectionState = "connected";
        console.log("SignalR Reconnected");
        this.retryCount = 0;
      });

      this.connection.onreconnecting(() => {
        this.connectionState = "reconnecting";
        console.log("SignalR Reconnecting...");
      });
    } catch (err) {
      this.connectionState = "disconnected";
      console.error("Error while starting connection:", err);
      this.retryCount++;

      if (this.retryCount <= MAX_RETRY_ATTEMPTS && !this.isExplicitlyStopped) {
        console.log(
          `Retrying connection (attempt ${this.retryCount}/${MAX_RETRY_ATTEMPTS})...`
        );
        setTimeout(() => this.startConnection(), RETRY_DELAY_MS);
      } else {
        console.error(
          "Max retry attempts reached. Stopping connection attempts."
        );
      }
    }
  }

  public async stopConnection(): Promise<void> {
    console.log("Stopping SignalR connection...");
    this.isExplicitlyStopped = true;
    this.connectionState = "disconnected";
    if (this.connection) {
      try {
        await this.connection.stop();
        console.log("SignalR connection stopped");
      } catch (err) {
        console.error("Error while stopping connection:", err);
      }
    }
    this.clearCallbacks();
  }

  public getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  private clearCallbacks(): void {
    this.notificationCallbacks = [];
    this.countCallbacks = [];
    // Cleared with the rest on logout, so a new session does not inherit the
    // previous user's screen subscriptions.
    this.notificationStateCallbacks = [];
    this.vanAlertCallbacks = [];
  }

  private registerHubMethods(): void {
    if (!this.connection) return;

    this.connection.on("ReceiveNotification", (notification: Notification) => {
      this.notificationCallbacks.forEach((callback) => callback(notification));
      toast.info(notification.body, {
        onClick: () => this.markAsRead(notification.id),
      });
    });

    // Read state changed for this user — possibly in another tab, or on their
    // phone. The server pushes these three; without handlers the notification
    // list and its badge drifted out of step with reality until a refetch, and
    // SignalR logged "No client method with the name ... found" on every one.
    //
    // The cached list/count are invalidated rather than patched in place: the
    // server has already pushed the authoritative UpdateUnreadCount alongside
    // each of these, and the next read goes to the API rather than to a stale
    // 30-second cache entry.
    const onReadStateChanged = () => {
      this.invalidateCache();
      this.notificationStateCallbacks.forEach((callback) => callback());
    };

    this.connection.on("NotificationRead", onReadStateChanged);
    this.connection.on("AllNotificationsRead", onReadStateChanged);
    this.connection.on("NotificationDismissed", onReadStateChanged);

    // Vehicle alerts. The mobile app already handled these; the web did not, so
    // office staff watching the fleet got nothing until they reloaded, and
    // SignalR logged "No client method with the name 'VanAlertNotification'
    // found" on every alert.
    //
    // Toasted directly here rather than left to subscribers: an alert is
    // time-sensitive and must surface on whatever page the user is on, not only
    // on the tracking screen. Severity picks the toast level so a Critical
    // alert is not styled like an informational one, and it does not auto-close.
    this.connection.on("VanAlertNotification", (payload: VanAlertPayload) => {
      const read = (...keys: string[]) => {
        for (const key of keys) {
          const value = payload?.[key];
          if (value !== undefined && value !== null && value !== "") return value;
        }
        return undefined;
      };

      const severity = String(read("severity", "Severity") ?? "").toLowerCase();
      const vehicle = read("vehicleNumber", "VehicleNumber");
      const message = read("message", "Message") ?? read("alertType", "AlertType") ?? "Vehicle alert";
      const text = vehicle ? `${vehicle}: ${message}` : String(message);

      this.vanAlertCallbacks.forEach((callback) => callback(payload));

      const options = { autoClose: false as const };
      if (severity.includes("critical") || severity.includes("high")) {
        toast.error(text, options);
      } else if (severity.includes("medium") || severity.includes("warn")) {
        toast.warning(text, options);
      } else {
        toast.info(text, options);
      }
    });

    // Permissions V2 — an admin changed this user's role grants. Pull the fresh
    // key set and swap it into Redux LIVE, so gating re-evaluates without a re-login.
    // (KEY-FIRST: we only need permissionKeys now; legacy rights are retired.)
    this.connection.on("PermissionNotification", async () => {
      try {
        const response = await axiosInstance.post(`${baseURL}/Account/RefreshPermissions`);
        const permissionData = response.data;

        if (!permissionData.isError && permissionData.dataModel) {
          const { permissionKeys, enabledModules, name, email, userId } = permissionData.dataModel;

          if (userId) localStorage.setItem("userid", userId.toString());
          // Merge, so tenantName/tenantId/isPlatformAdmin from login survive the refresh.
          if (name || email) store.dispatch(setUser({ ...(store.getState() as any).auth?.user, name, email }));

          // The single source of truth for gating. useSelector(permissionKeys)
          // subscribers (sidebar, PermissionButton) re-render immediately.
          store.dispatch(setPermissionKeys(Array.isArray(permissionKeys) ? permissionKeys : []));
          // A platform admin switching the company's modules sends this same event.
          store.dispatch(setEnabledModules(enabledModules));

          // Refetch whatever screen the user is on. Swapping the keys re-gates the
          // UI, but the ROWS on screen were fetched under the old grants and the
          // server filters by permission — so newly-allowed data stays invisible and
          // newly-revoked data stays on screen until the user navigates away.
          this.notifyAllDataChanged("PermissionsChanged");

          toast.info("Your permissions were updated.");
        } else {
          throw new Error(permissionData.responseMessage || "Permission refresh failed");
        }
      } catch (error: any) {
        // Non-fatal: on next navigation/login the fresh keys load anyway.
        console.error("Permission refresh failed:", error?.message || error);
      }
    });

    this.connection.on("UpdateUnreadCount", (count: number) => {
      this.countCallbacks.forEach((callback) => callback(count));
    });

    // Live data updates. The server sends one event for every module, with the
    // module in the payload, so a new screen needs no new event name — it just
    // subscribes to the module it shows. The server decides who receives it:
    // the company that owns the row plus every company it shared that module
    // with, so a shared screen updates for both sides.
    this.connection.on("DataChanged", (payload: DataChangedPayload) => {
      this.dataChangedCallbacks.forEach((callback) => callback(payload));
    });

    this.connection.onclose(async (error) => {
      this.connectionState = "disconnected";
      if (error) {
        console.error("Connection closed with error:", error);
      }
      if (!this.isExplicitlyStopped) {
        console.log("Attempting to reconnect...");
        await this.startConnection();
      }
    });
  }

  public subscribeToNotifications(
    callback: (notification: Notification) => void
  ): void {
    this.notificationCallbacks.push(callback);
  }

  /**
   * Refresh a screen when its data changes anywhere — including in a company
   * that shared this module with us.
   *
   * Returns the unsubscribe function, so a component can do:
   *   useEffect(() => notificationService.subscribeToDataChanged("clothing", reload), []);
   *
   * modules: which module prefixes this screen cares about ("clothing",
   * "employees", ...). Pass [] to receive every module.
   */
  public subscribeToDataChanged(
    modules: string | string[],
    callback: (payload: DataChangedPayload) => void
  ): () => void {
    const wanted = (Array.isArray(modules) ? modules : [modules])
      .map((m) => m.replace(/\.$/, "").toLowerCase())
      .filter(Boolean);

    // Coalesce a burst into one call. One user action can produce several events
    // (a save that writes to two modules, or two people saving at once), and a
    // subscriber reloading per event issued duplicate requests that queued behind
    // each other. Trailing edge, so the reload uses the latest state.
    let timer: ReturnType<typeof setTimeout> | null = null;
    let latest: DataChangedPayload | null = null;

    const handler = (payload: DataChangedPayload) => {
      const module = (payload.module || "").toLowerCase();
      // DATA_CHANGED_ALL bypasses the per-module filter: the caller's visibility
      // changed (permissions, or a share granted/revoked), which can affect any
      // screen regardless of which module it watches.
      if (module !== DATA_CHANGED_ALL && wanted.length > 0 && !wanted.includes(module)) return;

      latest = payload;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        const pending = latest;
        latest = null;
        if (pending) callback(pending);
      }, 300);
    };

    this.dataChangedCallbacks.push(handler);
    return () => {
      if (timer) clearTimeout(timer);
      this.dataChangedCallbacks = this.dataChangedCallbacks.filter((c) => c !== handler);
    };
  }

  public subscribeToUnreadCount(callback: (count: number) => void): void {
    this.countCallbacks.push(callback);
  }

  /**
   * Notified when this user's notifications were read or dismissed elsewhere —
   * another tab, or the mobile app. Returns an unsubscribe function.
   */
  public subscribeToNotificationStateChange(callback: () => void): () => void {
    this.notificationStateCallbacks.push(callback);
    return () => {
      this.notificationStateCallbacks = this.notificationStateCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Vehicle alerts, for a screen that wants to react beyond the toast this
   * service already shows (refresh a list, drop a pin on a map).
   * Returns an unsubscribe function.
   */
  /**
   * Make every subscribed screen refetch, whatever module it watches.
   *
   * For changes that alter what the API will RETURN rather than changing any row:
   * a permission grant, or a module being shared or revoked. The screen cannot
   * know which module that affects, and the honest answer is "possibly all of
   * them", so the wildcard below bypasses the per-module filter.
   */
  private notifyAllDataChanged(entity: string): void {
    this.dataChangedCallbacks.forEach((callback) =>
      callback({
        module: DATA_CHANGED_ALL,
        tenantId: 0,
        entity,
        action: "updated",
        at: new Date().toISOString(),
      })
    );
  }

  public subscribeToVanAlerts(callback: (payload: VanAlertPayload) => void): () => void {
    this.vanAlertCallbacks.push(callback);
    return () => {
      this.vanAlertCallbacks = this.vanAlertCallbacks.filter((cb) => cb !== callback);
    };
  }

  public unsubscribeFromNotifications(
    callback: (notification: Notification) => void
  ): void {
    this.notificationCallbacks = this.notificationCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  public unsubscribeFromUnreadCount(callback: (count: number) => void): void {
    this.countCallbacks = this.countCallbacks.filter((cb) => cb !== callback);
  }

  // Platform admins belong to no company: the server refuses the notification endpoints
  // (403), which logged an error and showed a "Failed to load notifications" toast on every
  // page. They have no notifications, so these calls are skipped for them.
  private isPlatformAdminSession(): boolean {
    return !!(store.getState() as any).auth?.user?.isPlatformAdmin;
  }

  // API Methods
  public async getUserNotifications(): Promise<Notification[]> {
    if (this.isPlatformAdminSession()) return [];
    if (this.notificationsCache && Date.now() - this.notificationsCache.at < this.CACHE_TTL) {
      return this.notificationsCache.data;
    }
    if (this.notificationsRequest) {
      return this.notificationsRequest;
    }

    this.notificationsRequest = (async () => {
      try {
        const response = await axiosInstance.post<{
          isError: boolean;
          dataModel: Notification[];
        }>(`${BASE_URL}/Notification/GetUserNotifications`);
        const data = response.data.isError ? [] : (response.data.dataModel ?? []);
        this.notificationsCache = { data, at: Date.now() };
        return data;
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
        toast.error("Failed to load notifications");
        return [];
      } finally {
        this.notificationsRequest = null;
      }
    })();

    return this.notificationsRequest;
  }

  public async getUnreadCount(): Promise<number> {
    if (this.isPlatformAdminSession()) return 0;
    if (this.unreadCountCache && Date.now() - this.unreadCountCache.at < this.CACHE_TTL) {
      return this.unreadCountCache.data;
    }
    if (this.unreadCountRequest) {
      return this.unreadCountRequest;
    }

    this.unreadCountRequest = (async () => {
      try {
        const response = await axiosInstance.post<{
          isError: boolean;
          dataModel: number;
        }>(`${BASE_URL}/Notification/GetUnreadCount`);
        const data = response.data.isError ? 0 : (response.data.dataModel ?? 0);
        this.unreadCountCache = { data, at: Date.now() };
        return data;
      } catch (error) {
        console.error("Failed to fetch unread count:", error);
        return 0;
      } finally {
        this.unreadCountRequest = null;
      }
    })();

    return this.unreadCountRequest;
  }

  public async markAsRead(deliveryId: number): Promise<boolean> {
    try {
      await axiosInstance.post(
        `${BASE_URL}/Notification/MarkAsRead?deliveryId=${deliveryId}`
      );
      this.invalidateCache();
      return true;
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      toast.error("Failed to mark notification as read");
      return false;
    }
  }

  public async markAllAsRead(): Promise<boolean> {
    try {
      await axiosInstance.post(`${BASE_URL}/Notification/MarkAllAsRead`);
      this.invalidateCache();
      return true;
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      toast.error("Failed to mark all notifications as read");
      return false;
    }
  }

  public async dismissNotification(deliveryId: number): Promise<boolean> {
    try {
      await axiosInstance.post(
        `${BASE_URL}/Notification/DismissNotification?deliveryId=${deliveryId}`
      );
      this.invalidateCache();
      return true;
    } catch (error) {
      console.error("Failed to dismiss notification:", error);
      toast.error("Failed to dismiss notification");
      return false;
    }
  }
}

export const notificationService = new NotificationService();
